import { Position } from '@xyflow/react'

export const WAYPOINT_GRID_SIZE = 20

export interface Waypoint {
  x: number
  y: number
}

export interface WaypointPathData {
  version: 1
  waypoints: Waypoint[]
}

export type AnchorKind = 'waypoint' | 'elbow'

export interface TaggedAnchor {
  x: number
  y: number
  kind: AnchorKind
  // waypointIndex set when kind === 'waypoint'
  waypointIndex?: number
  // rawSegmentIndex set when kind === 'elbow' — index of the raw segment
  // (in [source, ...waypoints, target]) this elbow belongs to.
  rawSegmentIndex?: number
}

export function snapToGrid(x: number, y: number, grid = WAYPOINT_GRID_SIZE): Waypoint {
  return {
    x: Math.round(x / grid) * grid,
    y: Math.round(y / grid) * grid,
  }
}

export function serializeWaypoints(waypoints: Waypoint[] | undefined | null): string | undefined {
  if (!waypoints || waypoints.length === 0) return undefined
  const payload: WaypointPathData = { version: 1, waypoints }
  return JSON.stringify(payload)
}

export function parseWaypoints(json: string | null | undefined): Waypoint[] {
  if (!json) return []
  try {
    const parsed = JSON.parse(json) as Partial<WaypointPathData>
    if (!parsed || !Array.isArray(parsed.waypoints)) return []
    return parsed.waypoints
      .filter((p): p is Waypoint => typeof p?.x === 'number' && typeof p?.y === 'number')
      .map((p) => ({ x: p.x, y: p.y }))
  } catch {
    return []
  }
}

type Axis = 'H' | 'V'

function axisFromPosition(pos: Position): Axis {
  return pos === Position.Left || pos === Position.Right ? 'H' : 'V'
}

export function orthogonalize(points: Waypoint[], firstAxis: Axis): Waypoint[] {
  if (points.length < 2) return points.slice()
  const out: Waypoint[] = []
  let axis: Axis = firstAxis
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    out.push(a)
    if (a.x !== b.x && a.y !== b.y) {
      const elbow: Waypoint = axis === 'H' ? { x: b.x, y: a.y } : { x: a.x, y: b.y }
      out.push(elbow)
      axis = axis === 'H' ? 'V' : 'H'
    } else if (a.x !== b.x) {
      axis = 'H'
    } else if (a.y !== b.y) {
      axis = 'V'
    }
  }
  out.push(points[points.length - 1])
  return dedupe(out)
}

function dedupe(points: Waypoint[]): Waypoint[] {
  const out: Waypoint[] = []
  for (const p of points) {
    const prev = out[out.length - 1]
    if (!prev || prev.x !== p.x || prev.y !== p.y) out.push(p)
  }
  return out
}

function pathFromOrtho(points: Waypoint[], radius: number): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`

  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]
    const corner = points[i]
    const next = points[i + 1]

    const inDx = Math.sign(corner.x - prev.x)
    const inDy = Math.sign(corner.y - prev.y)
    const outDx = Math.sign(next.x - corner.x)
    const outDy = Math.sign(next.y - corner.y)

    const inLen = Math.hypot(corner.x - prev.x, corner.y - prev.y)
    const outLen = Math.hypot(next.x - corner.x, next.y - corner.y)
    const r = Math.max(0, Math.min(radius, inLen / 2, outLen / 2))

    if (r === 0) {
      d += ` L ${corner.x},${corner.y}`
      continue
    }

    const startX = corner.x - inDx * r
    const startY = corner.y - inDy * r
    const endX = corner.x + outDx * r
    const endY = corner.y + outDy * r

    d += ` L ${startX},${startY}`
    d += ` Q ${corner.x},${corner.y} ${endX},${endY}`
  }
  const last = points[points.length - 1]
  d += ` L ${last.x},${last.y}`
  return d
}

export interface BuildWaypointPathParams {
  sourceX: number
  sourceY: number
  sourcePosition: Position
  targetX: number
  targetY: number
  targetPosition: Position
  waypoints: Waypoint[]
  borderRadius?: number
}

export interface BuiltPath {
  path: string
  labelX: number
  labelY: number
  orthoPoints: Waypoint[]
}

export function buildWaypointPath(params: BuildWaypointPathParams): BuiltPath {
  const {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    waypoints,
    borderRadius = 8,
  } = params

  // Always use custom orthogonal routing so the rendered path and computed
  // anchor positions are guaranteed to match.  The previous getSmoothStepPath
  // fallback for zero-waypoint edges produced a different route than
  // computeAnchors, causing elbow dots to float off the visible path.
  const raw: Waypoint[] = [
    { x: sourceX, y: sourceY },
    ...(waypoints ?? []),
    { x: targetX, y: targetY },
  ]

  const firstAxis = axisFromPosition(sourcePosition)
  const ortho = orthogonalize(raw, firstAxis)
  const path = pathFromOrtho(ortho, borderRadius)

  let labelX = (sourceX + targetX) / 2
  let labelY = (sourceY + targetY) / 2
  if (ortho.length >= 2) {
    const midIndex = Math.floor((ortho.length - 1) / 2)
    const a = ortho[midIndex]
    const b = ortho[midIndex + 1] ?? a
    labelX = (a.x + b.x) / 2
    labelY = (a.y + b.y) / 2
  }

  return { path, labelX, labelY, orthoPoints: ortho }
}

/**
 * Compute tagged interior anchors for the given source/waypoints/target.
 * Returns an entry for each explicit waypoint AND each auto-inserted elbow
 * on the orthogonal route. Source/target endpoints are excluded.
 *
 * Elbows carry their rawSegmentIndex (the index of the raw segment in
 * [source, ...waypoints, target] the elbow was inserted into) so that
 * dragging an elbow can convert it into a new explicit waypoint at the
 * correct position in the waypoints array.
 */
export function computeAnchors(
  sourceX: number,
  sourceY: number,
  sourcePosition: Position,
  targetX: number,
  targetY: number,
  waypoints: Waypoint[]
): TaggedAnchor[] {
  const raw: Waypoint[] = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ]
  if (raw.length < 2) return []

  const anchors: TaggedAnchor[] = []
  let axis: Axis = axisFromPosition(sourcePosition)

  for (let i = 0; i < raw.length - 1; i++) {
    const a = raw[i]
    const b = raw[i + 1]
    if (a.x !== b.x && a.y !== b.y) {
      const elbow: TaggedAnchor =
        axis === 'H'
          ? { x: b.x, y: a.y, kind: 'elbow', rawSegmentIndex: i }
          : { x: a.x, y: b.y, kind: 'elbow', rawSegmentIndex: i }
      anchors.push(elbow)
      axis = axis === 'H' ? 'V' : 'H'
    } else if (a.x !== b.x) {
      axis = 'H'
    } else if (a.y !== b.y) {
      axis = 'V'
    }

    // After handling the segment a→b, if b is an explicit waypoint (not the
    // terminal target), push it as a waypoint anchor.
    if (i + 1 < raw.length - 1) {
      anchors.push({
        x: b.x,
        y: b.y,
        kind: 'waypoint',
        waypointIndex: i, // waypoints[i] corresponds to raw[i+1]
      })
    }
  }

  return anchors
}

/**
 * For a click at (clickX, clickY) in flow space, find the index in the
 * waypoints array where a new waypoint should be inserted. Chooses the
 * raw segment whose midpoint is closest to the click.
 */
export function nearestRawSegmentIndex(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: Waypoint[],
  clickX: number,
  clickY: number
): number {
  const raw: Waypoint[] = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ]
  let bestDist = Infinity
  let bestIdx = 0
  for (let i = 0; i < raw.length - 1; i++) {
    const a = raw[i]
    const b = raw[i + 1]
    const midX = (a.x + b.x) / 2
    const midY = (a.y + b.y) / 2
    const d = Math.hypot(clickX - midX, clickY - midY)
    if (d < bestDist) {
      bestDist = d
      bestIdx = i
    }
  }
  return bestIdx
}

export interface OrthoSegment {
  x1: number
  y1: number
  x2: number
  y2: number
  /** 'H' = horizontal segment, 'V' = vertical segment */
  direction: 'H' | 'V'
  /** Index in the raw [source, ...waypoints, target] array — used to
   *  determine where a new waypoint should be inserted when dragging. */
  rawSegmentIndex: number
}

/**
 * Compute draggable segments from ortho points.  Each consecutive pair of
 * ortho points forms a segment.  We skip the first and last segments (the
 * stubs leaving the source/target handles) so the user can only bend
 * interior portions of the edge.
 *
 * `rawSegmentIndex` maps each ortho segment back to the index in the
 * original [source, ...waypoints, target] array so that inserting a new
 * waypoint lands in the correct slot.
 */
export function computeSegments(
  orthoPoints: Waypoint[],
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: Waypoint[],
): OrthoSegment[] {
  if (orthoPoints.length < 3) return []

  const raw: Waypoint[] = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ]

  const segments: OrthoSegment[] = []
  // Skip first and last segment (handle stubs)
  for (let i = 1; i < orthoPoints.length - 2; i++) {
    const a = orthoPoints[i]
    const b = orthoPoints[i + 1]
    const isH = a.y === b.y
    const len = isH ? Math.abs(b.x - a.x) : Math.abs(b.y - a.y)
    // Skip very short segments — not useful to drag
    if (len < 10) continue

    // Find which raw segment this ortho segment belongs to by finding
    // the raw segment whose midpoint is closest to this ortho segment's
    // midpoint — matching the approach used in nearestRawSegmentIndex.
    const midX = (a.x + b.x) / 2
    const midY = (a.y + b.y) / 2
    let bestIdx = 0
    let bestDist = Infinity
    for (let r = 0; r < raw.length - 1; r++) {
      const rMidX = (raw[r].x + raw[r + 1].x) / 2
      const rMidY = (raw[r].y + raw[r + 1].y) / 2
      const d = Math.hypot(midX - rMidX, midY - rMidY)
      if (d < bestDist) { bestDist = d; bestIdx = r }
    }

    segments.push({
      x1: a.x, y1: a.y,
      x2: b.x, y2: b.y,
      direction: isH ? 'H' : 'V',
      rawSegmentIndex: bestIdx,
    })
  }
  return segments
}

export function segmentMidpoints(orthoPoints: Waypoint[]): Waypoint[] {
  const mids: Waypoint[] = []
  for (let i = 0; i < orthoPoints.length - 1; i++) {
    const a = orthoPoints[i]
    const b = orthoPoints[i + 1]
    mids.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })
  }
  return mids
}
